import { IAs2Message, As2MessageStatus } from '@core/models/as2-message.model';

// ── Status state helpers ─────────────────────────────────────────────────────

/** Inbound message waiting for admin to approve or reject */
export function canApproveOrReject(msg?: IAs2Message | null): boolean {
    return msg?.direction === 'IN' && msg?.status === 'AWAITING_ADMIN_INPUT';
}

/** Outbound message that failed and can be retried */
export function canRetry(msg?: IAs2Message | null): boolean {
    return msg?.direction === 'OUT' && msg?.status === 'FAILED';
}

/** Message is in a terminal state — no further admin actions possible */
export function isAs2MessageClosed(msg?: IAs2Message | null): boolean {
    if (!msg) return false;
    return ['MDN_RECEIVED', 'SENT', 'ADMIN_REJECTED'].includes(msg.status ?? '');
}

/** Should the Detail link show on the list page? */
export function as2ShowDetailLink(msg: IAs2Message): boolean {
    // Always show for AWAITING_ADMIN_INPUT and FAILED — admin needs to act
    if (msg.status === 'AWAITING_ADMIN_INPUT' || msg.status === 'FAILED') return true;
    // Show for in-progress outbound too (monitoring)
    if (['PENDING', 'DISPATCHED', 'SENT'].includes(msg.status ?? '')) return true;
    return false;
}

// ── Human-readable labels ────────────────────────────────────────────────────

export function formatAs2Status(status?: string): string {
    switch (status) {
        case 'PENDING':               return 'Pending';
        case 'DISPATCHED':            return 'Dispatched to OpenAS2';
        case 'SENT':                  return msg_SENT_label(status);
        case 'FAILED':                return 'Failed';
        case 'MDN_RECEIVED':          return 'MDN Received ✓';
        case 'AWAITING_ADMIN_INPUT':  return 'Awaiting Admin Review';
        case 'ADMIN_REJECTED':        return 'Rejected';
        default:                      return status?.replace(/_/g, ' ') ?? '—';
    }
}

function msg_SENT_label(status: string): string {
    return 'Sent (awaiting MDN)';
}

export function formatAs2Direction(direction?: string): string {
    if (direction === 'OUT') return 'Outbound';
    if (direction === 'IN')  return 'Inbound';
    return direction ?? '—';
}

export function formatAs2MdnStatus(mdnStatus?: string, dispositionDetail?: string): string {
    switch (mdnStatus) {
        case 'PROCESSED':    return 'Processed ✓';
        case 'FAILED':
            if (dispositionDetail?.trim()) {
                const short = dispositionDetail.length > 80
                    ? `${dispositionDetail.slice(0, 77)}…`
                    : dispositionDetail;
                return `Failed — ${short}`;
            }
            return 'Failed ✗';
        case 'PENDING':      return 'Pending';
        case 'NOT_REQUIRED': return 'N/A (inbound)';
        default:             return mdnStatus ?? '—';
    }
}

export function formatFileSize(bytes?: number): string {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function formatAs2Timestamp(value?: string): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// ── Tailwind badge classes ───────────────────────────────────────────────────

export function as2StatusBadgeClass(status?: string): string {
    switch (status) {
        case 'MDN_RECEIVED':         return 'bg-green-100 text-green-800';
        case 'SENT':                 return 'bg-blue-100 text-blue-800';
        case 'DISPATCHED':           return 'bg-sky-100 text-sky-800';
        case 'PENDING':              return 'bg-slate-100 text-slate-600';
        case 'AWAITING_ADMIN_INPUT': return 'bg-amber-100 text-amber-900';
        case 'FAILED':               return 'bg-red-100 text-red-800';
        case 'ADMIN_REJECTED':       return 'bg-red-100 text-red-800';
        default:                     return 'bg-slate-100 text-slate-800';
    }
}

export function as2DirectionBadgeClass(direction?: string): string {
    return direction === 'IN'
        ? 'bg-purple-100 text-purple-800'
        : 'bg-indigo-100 text-indigo-800';
}

export function as2MdnStatusBadgeClass(mdnStatus?: string): string {
    switch (mdnStatus) {
        case 'PROCESSED':    return 'bg-green-100 text-green-800';
        case 'FAILED':       return 'bg-red-100 text-red-800';
        case 'PENDING':      return 'bg-amber-100 text-amber-900';
        default:             return 'bg-slate-100 text-slate-600';
    }
}
